// ===========================================
// Lesson Dialog Component
// ===========================================
// Form modal for creating/editing lessons

import { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Divider,
  Switch,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import FormModal from '../../common/FormModal';
import DriveFolderPicker from '../../googleDrive/DriveFolderPicker';
import HybridPatternConfig from './HybridPatternConfig';
import { termWeekCount, lessonWeekWindow } from '../../../utils/dateUtils';
import ClassWeeksConfig, { type ClassWeeksLessonType } from './ClassWeeksConfig';
import LessonWindowSelects from './LessonWindowSelects';
import { LessonFormData, GroupedLesson } from './lessons.types';
import { resolveAnchorTermId } from './lessons.helpers';
import type { TermLockedPredicate } from './lessons.helpers';
import { DAYS_OF_WEEK } from './lessons.constants';
import { formatTime, calculateEndTime, getLessonTypeColor } from '../../../api/lessons.api';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';

// ===========================================
// PROP TYPES
// ===========================================

interface Term {
  id: string;
  name: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
}

interface Location {
  id: string;
  name: string;
}

interface Room {
  id: string;
  name: string;
  capacity: number;
}

interface Instrument {
  id: string;
  name: string;
  color: string | null;
}

interface ProgramLevel {
  id: string;
  name: string;
  isActive: boolean;
  color: string | null;
}

interface Stream {
  id: string;
  name: string;
  isActive: boolean;
  color: string | null;
}

interface LessonType {
  id: string;
  name: string;
  type: string;
  defaultDuration: number;
  color: string | null;
  allowPartnerOverlap?: boolean;
}

// Partner classes: an eligible partner candidate for the picker. The parent page pre-filters
// to same teacher/term/day, INDIVIDUAL + allowPartnerOverlap, and not-already-partnered-elsewhere.
export interface PartnerCandidate {
  id: string;
  name: string;
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
  // Set on the currently-linked partner when it is no longer active (soft-deleted), so it stays
  // selectable (the Select needs a matching option) but is visibly annotated.
  inactive?: boolean;
}

interface Teacher {
  id: string;
  color: string | null;
  User: {
    firstName: string;
    lastName: string;
  };
}

interface LessonDuration {
  id: string;
  minutes: number;
}

interface LessonDialogProps {
  open: boolean;
  editingLesson: GroupedLesson | null;
  formData: LessonFormData;
  onFormDataChange: (updates: Partial<LessonFormData>) => void;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  conflictError?: string | null;
  overrideConflicts?: boolean;
  onOverrideConflictsChange?: (value: boolean) => void;
  // Reference data
  terms: Term[];
  /** Is this term LOCKED (its successor has started, no live reopen)? From `useTermLock`. */
  isLocked: TermLockedPredicate;
  /** "Term 3 is locked because Term 4 started on 12/10/2026." for a locked term id, else ''. */
  lockReasonOf?: (termId: string) => string;
  /** Has this term FINISHED on the SCHOOL's day? From `useTermLock` (drives the "(Ended)" label only). */
  isEnded?: (termId: string) => boolean;
  locations: Location[];
  rooms: Room[];
  instruments: Instrument[];
  programLevels: ProgramLevel[];
  streams: Stream[];
  lessonTypes: LessonType[];
  teachers: Teacher[];
  durations: LessonDuration[];
  selectedLessonType: LessonType | undefined;
  // Partner classes: eligible partners for the picker (parent-filtered). Only used when editing
  // an INDIVIDUAL lesson whose type has allowPartnerOverlap on.
  partnerCandidates?: PartnerCandidate[];
}

// ===========================================
// COMPONENT
// ===========================================

export default function LessonDialog({
  open,
  editingLesson,
  formData,
  onFormDataChange,
  onSubmit,
  onClose,
  isLoading,
  conflictError,
  overrideConflicts,
  onOverrideConflictsChange,
  terms,
  isLocked,
  lockReasonOf,
  isEnded,
  locations,
  rooms,
  instruments,
  programLevels,
  streams,
  lessonTypes,
  teachers,
  durations,
  selectedLessonType,
  partnerCandidates,
}: LessonDialogProps) {
  // Reopening a locked term is the owner's (`ownerOnly`): named by the school's title (slice 2).
  const ownerTitle = useOwnerTitle();
  // A LOCKED term is read-only (its successor has started and no admin reopen is live), and a class
  // SPANNING terms is only read-only when EVERY term it spans is locked - a Term 1-3 class is still
  // editable from Term 3. Keyed off the full span (`termLessons`), NOT `editingLesson.termId`: on a
  // grouped class that field is just the API-ordering seed (groupLessonsByProperties spreads
  // whichever row came back first), so it can name a locked term while unlocked terms remain.
  // Unknown terms (not in `terms`) are ignored, and an all-unknown span is NOT treated as locked,
  // so a stale/filtered terms list can never falsely lock the dialog. A term that has merely
  // FINISHED is still editable through the holidays - that is not "locked".
  const spannedTermIds = useMemo(
    () =>
      editingLesson
        ? editingLesson.termLessons?.length
          ? editingLesson.termLessons.map((tl) => tl.termId)
          : [editingLesson.termId]
        : [],
    [editingLesson]
  );
  const allLocked = useMemo(() => {
    if (!editingLesson) return false;
    const knownIds = spannedTermIds.filter((id) => terms.some((t) => t.id === id));
    return knownIds.length > 0 && knownIds.every((id) => isLocked(id));
  }, [editingLesson, spannedTermIds, terms, isLocked]);

  // Adding a term is always allowed (a future addition, not a retro edit): an all-locked class can
  // still be saved when an unlocked term is newly ticked. The add sends the FORM to bulkCreate, so
  // in the all-locked state every other field is disabled (below).
  const addedTermIds = formData.termIds.filter((id) => !spannedTermIds.includes(id) && !isLocked(id));
  const addingUnlockedTerm = !!editingLesson && addedTermIds.length > 0;
  // A plain class (INDIVIDUAL / GROUP / BAND) chooses its weeks; an empty "Choose weeks" list
  // blocks Save with the picker's inline error (#179 option 1 — it used to save nothing, silently).
  const classWeeksType: ClassWeeksLessonType | null =
    selectedLessonType && ['INDIVIDUAL', 'GROUP', 'BAND'].includes(selectedLessonType.type)
      ? (selectedLessonType.type as ClassWeeksLessonType)
      : null;
  const classWeeksEmpty =
    !!classWeeksType && formData.classWeeksPatternType === 'CUSTOM' && formData.classWeeks.length === 0;
  const submitDisabled = (allLocked && !addingUnlockedTerm) || classWeeksEmpty;
  const addedTermNames = addedTermIds
    .map((id) => terms.find((t) => t.id === id)?.name)
    .filter((n): n is string => !!n)
    .join(', ');
  const latestLockedSpannedId = useMemo(() => {
    const known = spannedTermIds
      .map((id) => terms.find((t) => t.id === id))
      .filter((t): t is Term => !!t)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    return known[known.length - 1]?.id;
  }, [spannedTermIds, terms]);

  // The term the class window applies to. Editing: the SAME anchor the page writes to
  // (`resolveAnchorTermId` — the seeding row's term can be an ended one). Creating: the first
  // selected term, which is what the page sends as `windowTermId`.
  const windowAnchorTerm = useMemo(() => {
    const anchorId = editingLesson
      ? resolveAnchorTermId(editingLesson, terms, isLocked)
      : (formData.termIds[0] ?? formData.termId);
    return terms.find((t) => t.id === anchorId);
  }, [editingLesson, terms, isLocked, formData.termIds, formData.termId]);

  // Creating: a week number belongs to the term it was picked for. When the anchor term
  // changes (the first term is unticked, or another becomes first) the held weeks are cleared
  // rather than silently re-applied to a different term on different dates (/qa F4).
  const windowAnchorId = windowAnchorTerm?.id;
  const previousWindowAnchorId = useRef(windowAnchorId);
  useEffect(() => {
    const previous = previousWindowAnchorId.current;
    previousWindowAnchorId.current = windowAnchorId;
    if (editingLesson || previous === undefined || previous === windowAnchorId) return;
    if (formData.startWeek !== null || formData.endWeek !== null) {
      onFormDataChange({ startWeek: null, endWeek: null });
    }
    // Only the anchor changing matters; the week values are read, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowAnchorId, editingLesson]);

  // The window as the hybrid / band week pickers caption it. Those pickers are SHARED by every
  // selected term while the window belongs to the anchor, so a week outside it is captioned
  // ("not scheduled in {anchor}"), never disabled — it still applies to the other terms.
  // undefined for a full-term class, so an unset window changes nothing.
  // Editing: the anchor term has been unticked — its row is deleted on save (/qa F3).
  const windowAnchorBeingRemoved =
    !!editingLesson && !!windowAnchorTerm && !formData.termIds.includes(windowAnchorTerm.id);

  // Nothing to caption when the anchor term is on its way out (its row, and so the window,
  // is deleted on save) - a caption about a term the admin just unticked is noise.
  const anchorWindow = useMemo(() => {
    if (
      !windowAnchorTerm ||
      windowAnchorBeingRemoved ||
      (formData.startWeek === null && formData.endWeek === null)
    ) {
      return undefined;
    }
    const { first, last } = lessonWeekWindow(
      formData,
      termWeekCount(windowAnchorTerm.startDate, windowAnchorTerm.endDate)
    );
    return { termName: windowAnchorTerm.name, first, last };
  }, [windowAnchorTerm, windowAnchorBeingRemoved, formData.startWeek, formData.endWeek]);

  // Calculate end time (only when schedule is confirmed and fields are set)
  const endTime = useMemo(
    () =>
      formData.hasSchedule && formData.startTime && formData.durationMins
        ? calculateEndTime(formData.startTime, formData.durationMins)
        : null,
    [formData.hasSchedule, formData.startTime, formData.durationMins]
  );

  // A band combination with no band lesson assigned is a valid "band TBD" state (e.g. after
  // the band lesson was force-deleted). We no longer block saving on it — the "Band to be
  // assigned" notice + the band selector live in HybridPatternConfig, so the admin can assign
  // a band later. Band billing/scheduling pauses (nulled bandWeeks) until they do.

  const handleScheduleToggle = () => {
    if (formData.hasSchedule) {
      onFormDataChange({ hasSchedule: false, dayOfWeek: null, startTime: null, durationMins: null });
    } else {
      onFormDataChange({ hasSchedule: true, dayOfWeek: 1, startTime: '09:00', durationMins: 45 });
    }
  };

  // Type-safe select change handlers
  type StringFields = 'lessonTypeId' | 'termId' | 'teacherId' | 'locationId' | 'roomId' | 'instrumentId' | 'programLevelId' | 'streamId' | 'patternType';
  type NumberFields = 'dayOfWeek' | 'durationMins';
  type StringArrayFields = 'termIds';

  const handleSelectChange = <K extends StringFields | NumberFields>(field: K) =>
    (event: SelectChangeEvent<LessonFormData[K]>) => {
      onFormDataChange({ [field]: event.target.value });
    };

  const handleStringArrayChange = <K extends StringArrayFields>(field: K) =>
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      onFormDataChange({
        [field]: typeof value === 'string' ? value.split(',') : value,
      });
    };

  return (
    <FormModal
      open={open}
      title={editingLesson ? 'Edit Class' : 'Add Class'}
      submitLabel={editingLesson ? 'Update' : 'Create'}
      loading={isLoading}
      maxWidth="md"
      onSubmit={onSubmit}
      onClose={onClose}
      submitDisabled={submitDisabled}
    >
      <Box sx={{ pt: 1 }}>
        {allLocked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            All of this class&apos;s terms are locked.
            {latestLockedSpannedId && lockReasonOf?.(latestLockedSpannedId)
              ? ` ${lockReasonOf(latestLockedSpannedId)}`
              : ''}{' '}
            You can add a new term. To change a locked term, your {ownerTitle} can reopen it in School
            Setup &rarr; Terms.
            {addingUnlockedTerm && (
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                Adding {addedTermNames} also enrols the students currently enrolled in this class (up to{' '}
                {formData.maxStudents}), from week 1.
              </Box>
            )}
          </Alert>
        )}
        {/* Term membership: the ONE control that stays live on an all-locked class (adding a term). */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="lesson-dialog-terms-label">Term{formData.termIds.length !== 1 ? 's' : ''}</InputLabel>
              <Select
                labelId="lesson-dialog-terms-label"
                multiple
                value={formData.termIds}
                label={`Term${formData.termIds.length !== 1 ? 's' : ''}`}
                onChange={handleStringArrayChange('termIds')}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((termId) => {
                      const term = terms.find((t) => t.id === termId);
                      return (
                        <Chip
                          key={termId}
                          label={term?.name || termId}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {terms.map((term) => {
                  const locked = isLocked(term.id);
                  // "Ended" is the SCHOOL's day (from the lock hook), never the UTC `isTermEnded`.
                  const ended = isEnded?.(term.id) ?? false;
                  return (
                    <MenuItem
                      key={term.id}
                      value={term.id}
                      // A LOCKED term is read-only whether or not it is ticked: it cannot be added
                      // to a class (the server 409s) and an already-ticked locked chip cannot be
                      // unticked (a membership edit never deletes a locked term's history). A term
                      // that has only FINISHED stays selectable until the next term starts.
                      disabled={locked}
                    >
                      {term.name}
                      {locked ? ' (Locked)' : ended ? ' (Ended)' : term.isActive ? ' (Active)' : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box
          component="fieldset"
          disabled={allLocked}
          sx={{
            border: 0,
            p: 0,
            m: 0,
            minWidth: 0,
            ...(allLocked ? { opacity: 0.6, pointerEvents: 'none' } : {}),
          }}
        >
        <Grid container spacing={2}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Class Name"
              value={formData.name}
              onChange={(e) => onFormDataChange({ name: e.target.value })}
              required
              fullWidth
              disabled={allLocked}
              placeholder="e.g., Monday Piano Group"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={allLocked}>
              <InputLabel>Class Type</InputLabel>
              <Select
                value={formData.lessonTypeId}
                label="Class Type"
                onChange={handleSelectChange('lessonTypeId')}
              >
                {lessonTypes.map((lt) => (
                  <MenuItem key={lt.id} value={lt.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: lt.color ?? `${getLessonTypeColor(lt.type)}.main`,
                          flexShrink: 0,
                        }}
                      />
                      {lt.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={allLocked}>
              <InputLabel>Instrument</InputLabel>
              <Select
                value={formData.instrumentId}
                label="Instrument"
                onChange={handleSelectChange('instrumentId')}
              >
                <MenuItem value="">None</MenuItem>
                {instruments.map((inst) => (
                  <MenuItem key={inst.id} value={inst.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {inst.color && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: inst.color, flexShrink: 0 }} />
                      )}
                      {inst.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={allLocked}>
              <InputLabel>Program Level</InputLabel>
              <Select
                value={formData.programLevelId}
                label="Program Level"
                onChange={handleSelectChange('programLevelId')}
              >
                <MenuItem value="">None</MenuItem>
                {programLevels
                  .filter((pl) => pl.isActive)
                  .map((pl) => (
                    <MenuItem key={pl.id} value={pl.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {pl.color && (
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: pl.color, flexShrink: 0 }} />
                        )}
                        {pl.name}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={allLocked}>
              <InputLabel>Stream</InputLabel>
              <Select
                value={formData.streamId}
                label="Stream"
                onChange={handleSelectChange('streamId')}
              >
                <MenuItem value="">None</MenuItem>
                {streams
                  .filter((s) => s.isActive)
                  .map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {s.color && (
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                        )}
                        {s.name}
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Max Students"
              type="number"
              value={formData.maxStudents === 0 ? '' : formData.maxStudents}
              onChange={(e) =>
                onFormDataChange({
                  maxStudents: e.target.value === '' ? 0 : parseInt(e.target.value),
                })
              }
              onBlur={() => {
                if (!formData.maxStudents || formData.maxStudents < 1) {
                  onFormDataChange({ maxStudents: 1 });
                }
              }}
              required
              fullWidth
              inputProps={{ min: 1, max: 30 }}
              disabled={allLocked || selectedLessonType?.type === 'INDIVIDUAL'}
            />
          </Grid>
          {/* Warning for group-type classes with maxStudents = 1 */}
          {selectedLessonType &&
            ['GROUP', 'BAND', 'HYBRID'].includes(selectedLessonType.type) &&
            formData.maxStudents === 1 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {selectedLessonType.type === 'HYBRID'
                  ? 'Hybrid classes typically have multiple students. Consider increasing the max students.'
                  : `${selectedLessonType.type.charAt(0) + selectedLessonType.type.slice(1).toLowerCase()} classes typically have multiple students. If this is a 1-on-1 class, consider using an Individual class type instead.`}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Teacher & Location
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={allLocked}>
              <InputLabel>Teacher</InputLabel>
              <Select
                value={formData.teacherId}
                label="Teacher"
                onChange={handleSelectChange('teacherId')}
              >
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {teacher.color && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: teacher.color, flexShrink: 0 }} />
                      )}
                      {teacher.User.firstName} {teacher.User.lastName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={allLocked}>
              <InputLabel>Location</InputLabel>
              <Select
                value={formData.locationId}
                label="Location"
                onChange={handleSelectChange('locationId')}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={allLocked || !formData.locationId}>
              <InputLabel>Room</InputLabel>
              <Select
                value={formData.roomId}
                label="Room"
                onChange={handleSelectChange('roomId')}
              >
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.name} (Cap: {room.capacity})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Schedule
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hasSchedule}
                    onChange={handleScheduleToggle}
                    size="small"
                    disabled={allLocked}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Schedule confirmed
                  </Typography>
                }
                labelPlacement="start"
                sx={{ ml: 0, mr: 0 }}
              />
            </Box>
          </Grid>
          {formData.hasSchedule ? (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={allLocked}>
                  <InputLabel>Day of Week</InputLabel>
                  <Select
                    value={formData.dayOfWeek ?? 1}
                    label="Day of Week"
                    onChange={handleSelectChange('dayOfWeek')}
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <MenuItem key={day.value} value={day.value}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={allLocked}>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={formData.durationMins ?? 45}
                    label="Duration"
                    onChange={handleSelectChange('durationMins')}
                  >
                    {durations.map((d) => (
                      <MenuItem key={d.id} value={d.minutes}>
                        {d.minutes} mins
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={formData.startTime ?? '09:00'}
                  onChange={(e) => onFormDataChange({ startTime: e.target.value })}
                  required
                  fullWidth
                  disabled={allLocked}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 900 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Time"
                  value={endTime ? formatTime(endTime) : ''}
                  disabled
                  fullWidth
                  helperText="Calculated from duration"
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                Schedule can be set later once the teacher or room has been confirmed.
              </Alert>
            </Grid>
          )}
          {/* Class window — OUTSIDE the hasSchedule block: a "schedule TBD" class may still start late */}
          <LessonWindowSelects
            startWeek={formData.startWeek}
            endWeek={formData.endWeek}
            onChange={onFormDataChange}
            anchorTerm={windowAnchorTerm}
            selectedTermCount={formData.termIds.length || 1}
            dayOfWeek={formData.hasSchedule ? formData.dayOfWeek : null}
            disabled={allLocked}
            anchorBeingRemoved={windowAnchorBeingRemoved}
          />
          {/* Hybrid Pattern Section */}
          {selectedLessonType?.type === 'HYBRID' && (
            <HybridPatternConfig
              formData={formData}
              onFormDataChange={onFormDataChange}
              termId={formData.termIds[0] ?? formData.termId}
              termIds={formData.termIds}
              termNames={Object.fromEntries(terms.map((t) => [t.id, t.name]))}
              termWeeks={Object.fromEntries(terms.map((t) => [t.id, termWeekCount(t.startDate, t.endDate)]))}
              anchorWindow={anchorWindow}
              disabled={allLocked}
            />
          )}

          {/* Class weeks — INDIVIDUAL / GROUP ("Lesson weeks", fortnightly quick-fill) and BAND (rehearsals) */}
          {classWeeksType && (
            <ClassWeeksConfig
              lessonType={classWeeksType}
              lessonId={editingLesson?.id ?? ''}
              termId={formData.termIds[0] ?? formData.termId}
              selectedTermCount={formData.termIds.length || 1}
              termIds={formData.termIds}
              termNames={Object.fromEntries(terms.map((t) => [t.id, t.name]))}
              termWeeks={Object.fromEntries(terms.map((t) => [t.id, termWeekCount(t.startDate, t.endDate)]))}
              anchorWindow={anchorWindow}
              disabled={allLocked}
              formData={{
                patternType: formData.classWeeksPatternType,
                weeks: formData.classWeeks,
              }}
              onFormDataChange={(updates) =>
                onFormDataChange({
                  ...(updates.patternType !== undefined && { classWeeksPatternType: updates.patternType }),
                  ...(updates.weeks !== undefined && { classWeeks: updates.weeks }),
                })
              }
            />
          )}

          {/* Partner Class Section — editing an INDIVIDUAL type with partner classes enabled.
              Pairing is edit-only (a brand-new class has no id yet and the partner must exist),
              so it is hidden on create. */}
          {editingLesson &&
            selectedLessonType?.type === 'INDIVIDUAL' &&
            selectedLessonType?.allowPartnerOverlap && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Partner Class
              </Typography>
              <FormControl fullWidth disabled={allLocked}>
                <InputLabel>Partner class</InputLabel>
                <Select
                  value={formData.partnerLessonId}
                  label="Partner class"
                  onChange={(e) => onFormDataChange({ partnerLessonId: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {partnerCandidates?.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                      {c.startTime && ` (${formatTime(c.startTime)}${c.endTime ? `–${formatTime(c.endTime)}` : ''})`}
                      {c.inactive && ' — inactive'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ mt: 1 }}>
                Pairing this class with another individual class of the same teacher, term, and day lets
                the two overlap (e.g. 4:00–5:00 and 4:30–5:30). Only classes eligible to be a partner are
                listed. Set to “None” to unpair.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => onFormDataChange({ description: e.target.value })}
              fullWidth
              disabled={allLocked}
              multiline
              rows={2}
              placeholder="Optional notes about this class..."
            />
          </Grid>

          {/* Google Drive Folder - only show when editing */}
          {editingLesson && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Google Drive Folder
              </Typography>
              <DriveFolderPicker lessonId={editingLesson.id} />
            </Grid>
          )}

          {/* Status toggle - only show when editing */}
          {editingLesson && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => onFormDataChange({ isActive: e.target.checked })}
                    disabled={allLocked}
                  />
                }
                label={formData.isActive ? 'Active' : 'Inactive'}
              />
            </Grid>
          )}
        </Grid>
        </Box>

        {/* Conflict warning with override option */}
        {conflictError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Conflict Detected
            </Typography>
            <Typography variant="body2">{conflictError}</Typography>
            {onOverrideConflictsChange && (
              <FormControlLabel
                sx={{ mt: 1, mb: 0, ml: 1 }}
                control={
                  <Switch
                    checked={overrideConflicts ?? false}
                    onChange={(e) => onOverrideConflictsChange(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="medium">
                    Override conflict and save anyway
                  </Typography>
                }
              />
            )}
          </Alert>
        )}
      </Box>
    </FormModal>
  );
}
